import React, { useMemo, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Chip,
    Container,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Typography,
    useTheme,
    alpha,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
// Optional: if you have it in your project
// import { SectionHeading } from "./utils";

const FAQ = () => {
    const theme = useTheme();
    const [query, setQuery] = useState("");
    const [expandedKeys, setExpandedKeys] = useState([]); // array of indices that are open

    const faqItems = useMemo(() => [
        {
            q: "How do I create an account?",
            a: `Click on the "Sign Up" button in the top right corner and follow the registration process. You'll need to provide a valid email address, a business name and create a password.`,
            tag: "Account",
        },
        {
            q: "Do you have a referral program?",
            a: "Yes, we do. When you create an account, you get a link that you can share with others and earn 20% of their subscription amount. You also get a free trial account to enable you do a demo on the available services.",
            tag: "Billing",
        },
        {
            q: "What payment methods do you accept?",
            a: "At the moment, we only accept payment from M-Pesa. We are working on incorporating more payment methods.",
            tag: "Billing",
        },
        {
            q: "Can I edit my QR code after printing it?",
            a: "Yes! Qr-Flex let you update the destination URL/content anytime without reprinting the code.",
            tag: "QR",
        },
        {
            q: "What happens if my QR code gets damaged?",
            a: "Our QR codes include error correction, so they remain scannable even if partially damaged. You can also regenerate the same code.",
            tag: "QR",
        },
        {
            q: "What industries use dynamic QR codes?",
            a: "Restaurants (menus), retail (product info), healthcare (patient records), and marketing (campaign tracking).",
            tag: "Use cases",
        },
        {
            q: "How do I use QR codes in print marketing?",
            a: "Add them to posters, brochures, or business cards to drive traffic to landing pages, videos, or special offers.",
            tag: "Marketing",
        },
        {
            q: "Is there a free plan?",
            a: "We offer a free trial with basic features and limited scans. Upgrade to premium plans for unlimited scans and advanced analytics.",
            tag: "Plans",
        },
        {
            q: "How do I reset my password?",
            a: `Go to the login page and click "Forgot Password". Enter your email address and we'll send an email with instructions to reset your password.`,
            tag: "Account",
        },
    ], []);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return faqItems;
        return faqItems.filter(
            (it) =>
                it.q.toLowerCase().includes(q) ||
                it.a.toLowerCase().includes(q) ||
                (it.tag || "").toLowerCase().includes(q)
        );
    }, [faqItems, query]);

    const expandAll = () => setExpandedKeys(filtered.map((_, i) => i));
    const collapseAll = () => setExpandedKeys([]);

    const toggleOne = (index) => (_e, isExpanded) => {
        setExpandedKeys((prev) =>
            isExpanded ? [...new Set([...prev, index])] : prev.filter((k) => k !== index)
        );
    };

    const gradientBorder =
        theme.palette.mode === "light"
            ? "linear-gradient(135deg, rgba(99,102,241,0.35), rgba(236,72,153,0.2))"
            : "linear-gradient(135deg, rgba(129,140,248,0.5), rgba(244,114,182,0.3))";

    return (
        <Box
            component="section"
            sx={{
                py: { xs: 4, sm: 6 },
                position: "relative",
                isolation: "isolate",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: { xs: "8% 4% auto", md: "12% 10% auto" },
                    height: { xs: 160, sm: 210 },
                    background:
                        theme.palette.mode === "light"
                            ? "radial-gradient(circle at center, rgba(129,140,248,0.22), rgba(236,72,153,0.08) 55%, transparent 75%)"
                            : "radial-gradient(circle at center, rgba(129,140,248,0.28), rgba(236,72,153,0.14) 55%, transparent 75%)",
                    filter: "blur(8px)",
                    zIndex: -1,
                    pointerEvents: "none",
                },
            }}
        >
            <Container maxWidth="md">
                <Box
                    sx={{
                        p: { xs: 0.75, sm: 1.25 },
                        borderRadius: { xs: 3, sm: 4 },
                        border: "1px solid transparent",
                        backgroundImage: `${gradientBorder}, ${theme.palette.background.paper}`,
                        backgroundOrigin: "border-box",
                        backgroundClip: "padding-box, border-box",
                        boxShadow:
                            theme.palette.mode === "light"
                                ? "0 16px 32px rgba(79,70,229,0.12)"
                                : "0 16px 32px rgba(8,10,28,0.35)",
                    }}
                >
                    {/* Controls row */}
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        mb: { xs: 1.5, sm: 2.5 },
                        p: { xs: 0.75, sm: 1.25 },
                        border: (t) => `1px solid ${alpha(t.palette.primary.main, t.palette.mode === "light" ? 0.2 : 0.32)}`,
                        backdropFilter: "blur(18px)",
                        borderRadius: { xs: 4, sm: 3.5 },
                        background:
                            theme.palette.mode === "light"
                                ? "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(248,250,252,0.92))"
                                : "linear-gradient(135deg, rgba(17,24,39,0.86), rgba(30,41,59,0.75))",
                    }}
                >
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search FAQs (e.g. payment, QR, password)…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="primary" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: { xs: 2.5, sm: 999 },
                                bgcolor: alpha(theme.palette.background.default, theme.palette.mode === "light" ? 0.65 : 0.2),
                            },
                        }}
                    />

                    <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
                        <IconButton
                            aria-label="Expand all"
                            onClick={expandAll}
                            sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 } }}
                        >
                            <UnfoldMoreIcon />
                        </IconButton>
                        <IconButton
                            aria-label="Collapse all"
                            onClick={collapseAll}
                            sx={{ border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`, width: { xs: 34, sm: 38 }, height: { xs: 34, sm: 38 } }}
                        >
                            <UnfoldLessIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                    {/* FAQs */}
                <Stack spacing={{ xs: 1.25, sm: 1.5 }}>
                    {filtered.map((faq, idx) => (
                        <Accordion
                            key={`${faq.q}-${idx}`}
                            disableGutters
                            elevation={0}
                            expanded={expandedKeys.includes(idx)}
                            onChange={toggleOne(idx)}
                            sx={{
                                border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.15 : 0.35)}`,
                                borderRadius: { xs: 4, sm: 3.5 },
                                background:
                                    theme.palette.mode === "light"
                                        ? "linear-gradient(120deg, rgba(255,255,255,0.95), rgba(248,250,252,0.95))"
                                        : "linear-gradient(120deg, rgba(30,41,59,0.82), rgba(15,23,42,0.85))",
                                transition: "transform .2s ease, box-shadow .2s, border-color .2s",
                                overflow: "hidden",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 16px 34px rgba(79,70,229,0.16)",
                                },
                                "&:before": { display: "none" },
                                // subtle left accent when expanded
                                ...(expandedKeys.includes(idx) && {
                                    borderColor:
                                        theme.palette.mode === "light"
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.dark,
                                    boxShadow: "0 18px 36px rgba(79,70,229,0.2)",
                                }),
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                                sx={{
                                    backgroundColor: "transparent",
                                    px: { xs: 1.75, sm: 2 },
                                    py: { xs: 0.9, sm: 1.2 },
                                    "&.Mui-expanded": {
                                        borderBottomLeftRadius: 0,
                                        borderBottomRightRadius: 0,
                                    },
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ width: "100%", gap: 1, minWidth: 0 }}
                                >
                                    <Typography
                                        component="h3"
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: { xs: "0.98rem", sm: "1.1rem" },
                                            lineHeight: 1.35,
                                            pr: 0.75,
                                        }}
                                    >
                                        {faq.q}
                                    </Typography>
                                    {faq.tag && (
                                        <Chip
                                            size="small"
                                            label={faq.tag}
                                            variant="outlined"
                                            sx={{ display: { xs: "none", sm: "inline-flex" }, borderRadius: 999 }}
                                        />
                                    )}
                                </Stack>
                            </AccordionSummary>

                            <AccordionDetails
                                sx={{
                                    backgroundColor: "transparent",
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    px: { xs: 1.75, sm: 2 },
                                    pt: { xs: 1, sm: 1.25 },
                                    pb: { xs: 1.25, sm: 1.75 },
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: ".95rem", sm: "1rem" }, lineHeight: 1.7 }}
                                >
                                    {faq.a}
                                </Typography>

                                {/* Mobile tag mirror inside details */}
                                {faq.tag && (
                                    <Chip
                                        size="small"
                                        label={faq.tag}
                                        variant="outlined"
                                        sx={{ mt: 1, display: { xs: "inline-flex", sm: "none" }, borderRadius: 999 }}
                                    />
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}

                    {/* Empty state if search filters out everything */}
                    {filtered.length === 0 && (
                        <Box
                            sx={{
                                p: { xs: 2, sm: 3 },
                                borderRadius: { xs: 4, sm: 3.5 },
                                textAlign: "center",
                                border: (t) => `1px solid ${t.palette.mode === "light" ? "rgba(99,102,241,0.2)" : t.palette.divider}`,
                                backdropFilter: "blur(12px)",
                            }}
                        >
                            <Typography sx={{ mb: 0.5, fontWeight: 700 }}>
                                No results for “{query}”
                            </Typography>
                            <Typography color="text.secondary">
                                Try a different keyword like <strong>payment</strong>, <strong>QR</strong>, or{" "}
                                <strong>password</strong>.
                            </Typography>
                        </Box>
                    )}
                    </Stack>
                </Box>
            </Container>
        </Box>
    );
};

export default FAQ;
